import { z } from 'zod'

import { globalSchema } from './globalSchema.ts'

export const dbAccountSchema = z.object({
  id: globalSchema.mysqlCHAR,
  slug: globalSchema.mysqlVARCHAR.nullable(),
  unique_id: globalSchema.mysqlVARCHAR.nullable(),
  referral_code: globalSchema.mysqlVARCHAR.nullable(),
  pusher_secret: globalSchema.mysqlVARCHAR.nullable(),
  name: globalSchema.mysqlVARCHAR.nullable(),
  email: globalSchema.mysqlVARCHAR.nullable(),
  billing_email: globalSchema.mysqlVARCHAR.nullable(),
  change_billing_email_too: globalSchema.mysqlTINYINT,
  billable: globalSchema.mysqlTINYINT,
  company: globalSchema.mysqlVARCHAR,
  country_id: globalSchema.mysqlCHAR.nullable(),
  plan_id: globalSchema.mysqlCHAR.nullable(),
  secretKey: globalSchema.mysqlVARCHAR.describe('Still in use.'),
  signatureAuth: globalSchema.mysqlTINYINT,
  transcoding_result_expiry: globalSchema.mysqlCHAR,
  assembly_status_expiry: globalSchema.mysqlCHAR,
  require_template_id: globalSchema.mysqlTINYINT,
  auto_replay_crashed_assemblies: globalSchema.mysqlTINYINT,
  save_template_in_assembly_json: globalSchema.mysqlTINYINT,
  use_case: globalSchema.mysqlTEXT,
  address_1: globalSchema.mysqlVARCHAR,
  address_2: globalSchema.mysqlVARCHAR,
  zip: globalSchema.mysqlVARCHAR,
  city: globalSchema.mysqlVARCHAR,
  state: globalSchema.mysqlVARCHAR,
  vat_id: globalSchema.mysqlVARCHAR,
  bill_limit: globalSchema.mysqlDECIMAL.nullable(),
  plan_exceeded: globalSchema.mysqlTINYINT,
  bill_limit_reached: globalSchema.mysqlTINYINT,
  stop_processing_on_bill_limit: globalSchema.mysqlTINYINT.nullable(),
  assembly_rate_limit: globalSchema.mysqlSMALLINT.nullable(),
  concurrent_assembly_rate_limit: globalSchema.mysqlSMALLINT.nullable(),
  cancelled: globalSchema.mysqlDATETIME.nullable(),
  cancel_reason: globalSchema.mysqlTEXT,
  created: globalSchema.mysqlDATETIME,
  modified: globalSchema.mysqlDATETIME,
  deleted: globalSchema.mysqlDATETIME.nullable(),
  last_used: globalSchema.mysqlDATETIME.nullable(),
  referral_visits: globalSchema.mysqlINT,
  revenue_forecast: globalSchema.mysqlDECIMAL,
  unsuccessful_charge_attempts: globalSchema.mysqlINT,
  default_imagemagick_stack: globalSchema.mysqlVARCHAR.nullable(),
  default_ffmpeg_stack: globalSchema.mysqlVARCHAR.nullable(),
  signup_discount: globalSchema.mysqlDECIMAL,
  usage_forecast: globalSchema.mysqlDECIMAL,
  lifetime_usage: globalSchema.mysqlDECIMAL.nullable(),
  uses_invoicing: globalSchema.mysqlTINYINT.nullable(),
  partner: globalSchema.mysqlVARCHAR.nullable(),
  premium_config: globalSchema.mysqlTEXT,
  can_be_auto_cancelled: globalSchema.mysqlTINYINT,
  via_referer: globalSchema.mysqlVARCHAR.nullable(),
  via_utm_source: globalSchema.mysqlVARCHAR.nullable(),
  via_utm_medium: globalSchema.mysqlVARCHAR.nullable(),
  via_utm_campaign: globalSchema.mysqlVARCHAR.nullable(),
  via_utm_term: globalSchema.mysqlVARCHAR.nullable(),
  via_utm_content: globalSchema.mysqlVARCHAR.nullable(),
  linkback_url: globalSchema.mysqlVARCHAR,
  payment_provider: globalSchema.mysqlVARCHAR.nullable(),
  stripe_customer_id: globalSchema.mysqlTEXT.nullable(),
})
export type DbAccount = z.infer<typeof dbAccountSchema>
export const dbAccountUserSchema = z.object({
  id: globalSchema.mysqlCHAR,
  account_id: globalSchema.mysqlCHAR.nullable(),
  user_id: globalSchema.mysqlCHAR.nullable(),
  role: globalSchema.mysqlVARCHAR.nullable(),
  app_icon_color: globalSchema.mysqlVARCHAR.nullable(),
  email_when_close_to_plan_limit: globalSchema.mysqlTINYINT,
  last_idle_email: globalSchema.mysqlINT,
  last_idle_email_on: globalSchema.mysqlDATETIME.nullable(),
  last_close_to_plan_limit_email_on: globalSchema.mysqlDATETIME.nullable(),
  last_bill_limit_reached_email_on: globalSchema.mysqlDATETIME.nullable(),
  last_plan_exceeded_email_on: globalSchema.mysqlDATETIME.nullable(),
  email_on_assembly_errors: globalSchema.mysqlTINYINT,
  email_on_assembly_warnings: globalSchema.mysqlTINYINT,
  created: globalSchema.mysqlDATETIME.nullable(),
  modified: globalSchema.mysqlDATETIME.nullable(),
  deleted: globalSchema.mysqlDATETIME.nullable(),
})
export type DbAccountUser = z.infer<typeof dbAccountUserSchema>
export const dbActivitySchema = z.object({
  id: globalSchema.mysqlCHAR,
  referer: globalSchema.mysqlVARCHAR.nullable(),
  ip: globalSchema.mysqlVARCHAR.nullable(),
  url: globalSchema.mysqlVARCHAR.nullable(),
  type: globalSchema.mysqlVARCHAR.nullable(),
  account_id: globalSchema.mysqlCHAR.nullable(),
  user_id: globalSchema.mysqlCHAR.nullable(),
  email: globalSchema.mysqlVARCHAR.nullable(),
  agent: globalSchema.mysqlVARCHAR.nullable(),
  created: globalSchema.mysqlDATETIME.nullable(),
  modified: globalSchema.mysqlDATETIME.nullable(),
  deleted: globalSchema.mysqlDATETIME.nullable(),
})
export type DbActivity = z.infer<typeof dbActivitySchema>
export const dbAnnouncementSchema = z.object({
  id: globalSchema.mysqlCHAR,
  read_count: globalSchema.mysqlINT,
  title: globalSchema.mysqlVARCHAR.nullable(),
  body: globalSchema.mysqlTEXT.nullable(),
  created: globalSchema.mysqlDATETIME.nullable(),
  modified: globalSchema.mysqlDATETIME.nullable(),
  deleted: globalSchema.mysqlDATETIME.nullable(),
})
export type DbAnnouncement = z.infer<typeof dbAnnouncementSchema>
export const dbApiAuthKeySchema = z.object({
  id: globalSchema.mysqlCHAR,
  workspace_id: globalSchema.mysqlCHAR.nullable(),
  auth_key: globalSchema.mysqlVARCHAR.nullable(),
  auth_secret: globalSchema.mysqlVARCHAR.nullable(),
  expires: globalSchema.mysqlDATETIME.nullable(),
  is_active: globalSchema.mysqlTINYINT,
  is_internal: globalSchema.mysqlTINYINT.nullable(),
  is_allowed_for_smartcdn: globalSchema.mysqlTINYINT,
  signature_algo: globalSchema.mysqlVARCHAR.nullable().describe('This is the new field.'),
  scope: globalSchema.mysqlVARCHAR.nullable(),
  description: globalSchema.mysqlVARCHAR,
  can_show_auth_secret: globalSchema.mysqlTINYINT,
  last_used: globalSchema.mysqlDATETIME.nullable(),
  created: globalSchema.mysqlDATETIME.nullable(),
  modified: globalSchema.mysqlDATETIME.nullable(),
  deleted: globalSchema.mysqlDATETIME.nullable(),
})
export type DbApiAuthKey = z.infer<typeof dbApiAuthKeySchema>
export const dbAssemblySchema = z.object({
  int_id: globalSchema.mysqlINT,
  id: globalSchema.mysqlCHAR,
  parent_id: globalSchema.mysqlCHAR.nullable(),
  account_id: globalSchema.mysqlCHAR.nullable(),
  api_auth_key_id: globalSchema.mysqlCHAR.nullable(),
  ok: globalSchema.mysqlVARCHAR.nullable(),
  state: globalSchema.mysqlTINYINT.nullable(),
  error: globalSchema.mysqlVARCHAR.nullable(),
  template_id: globalSchema.mysqlCHAR.nullable(),
  region: globalSchema.mysqlVARCHAR.nullable(),
  cluster: globalSchema.mysqlVARCHAR,
  transloadit_client: globalSchema.mysqlVARCHAR.nullable(),
  notify_url: globalSchema.mysqlVARCHAR.nullable(),
  is_urltransform: globalSchema.mysqlTINYINT.nullable(),
  input_file_sizes: globalSchema.mysqlBIGINT.nullable(),
  total_output_file_size: globalSchema.mysqlBIGINT.nullable(),
  num_input_files: globalSchema.mysqlINT.nullable(),
  num_output_files: globalSchema.mysqlINT.nullable(),
  bytes_usage: globalSchema.mysqlINT.nullable(),
  files: globalSchema.mysqlVARCHAR.nullable(),
  instance: globalSchema.mysqlVARCHAR.nullable(),
  redirect_url: globalSchema.mysqlVARCHAR.nullable(),
  uploaded_bytes: globalSchema.mysqlBIGINT.nullable(),
  upload_duration: globalSchema.mysqlFLOAT.nullable(),
  execution_start: globalSchema.mysqlDATETIME,
  execution_duration: globalSchema.mysqlFLOAT.nullable(),
  live_queue_duration: globalSchema.mysqlFLOAT.nullable(),
  batch_queue_duration: globalSchema.mysqlFLOAT.nullable(),
  urltransform_queue_duration: globalSchema.mysqlFLOAT.nullable(),
  request_signature: globalSchema.mysqlVARCHAR.nullable(),
  warning_count: globalSchema.mysqlINT.nullable(),
  created: globalSchema.mysqlDATETIME,
  created_ts: globalSchema.mysqlINT,
  finished_ts: globalSchema.mysqlINT,
  updated: globalSchema.mysqlDATETIME,
})
export type DbAssembly = z.infer<typeof dbAssemblySchema>
export const dbAssemblyStatsSchema = z.object({
  int_id: globalSchema.mysqlINT,
  id: globalSchema.mysqlCHAR,
  account_id: globalSchema.mysqlCHAR.nullable(),
  granularity_secs: globalSchema.mysqlINT,
  template_id: globalSchema.mysqlCHAR.nullable(),
  region: globalSchema.mysqlVARCHAR.nullable(),
  data_from: globalSchema.mysqlDATETIME.nullable(),
  data_to: globalSchema.mysqlDATETIME.nullable(),
  data_from_ts: globalSchema.mysqlINT,
  data_to_ts: globalSchema.mysqlINT,
  total_num_input_files: globalSchema.mysqlINT,
  avg_num_input_files: globalSchema.mysqlINT,
  total_input_file_sizes: globalSchema.mysqlBIGINT,
  avg_output_file_sizes: globalSchema.mysqlBIGINT,
  total_num_output_files: globalSchema.mysqlINT,
  avg_num_output_files: globalSchema.mysqlINT,
  total_output_file_sizes: globalSchema.mysqlBIGINT,
  avg_input_file_sizes: globalSchema.mysqlBIGINT,
  total_bytes_usage: globalSchema.mysqlBIGINT,
  avg_bytes_usage: globalSchema.mysqlBIGINT,
  num_assemblies: globalSchema.mysqlINT,
  num_successful_assemblies: globalSchema.mysqlINT,
  num_cancelled_assemblies: globalSchema.mysqlINT,
  num_request_aborted_assemblies: globalSchema.mysqlINT,
  num_erroneous_assemblies: globalSchema.mysqlINT,
  total_num_uploaded_bytes: globalSchema.mysqlBIGINT,
  total_upload_duration: globalSchema.mysqlFLOAT,
  avg_num_uploaded_bytes: globalSchema.mysqlBIGINT,
  avg_upload_duration: globalSchema.mysqlFLOAT,
  avg_upload_speed_mbit_per_s: globalSchema.mysqlFLOAT,
  total_execution_duration: globalSchema.mysqlFLOAT,
  avg_execution_duration: globalSchema.mysqlFLOAT,
  avg_execution_speed_mbit_per_s: globalSchema.mysqlFLOAT,
  total_live_queue_duration: globalSchema.mysqlFLOAT,
  avg_live_queue_duration: globalSchema.mysqlFLOAT,
  total_batch_queue_duration: globalSchema.mysqlFLOAT,
  avg_batch_queue_duration: globalSchema.mysqlFLOAT,
  total_urltransform_queue_duration: globalSchema.mysqlFLOAT,
  avg_urltransform_queue_duration: globalSchema.mysqlFLOAT,
  created: globalSchema.mysqlDATETIME,
  updated: globalSchema.mysqlDATETIME,
})
export type DbAssemblyStats = z.infer<typeof dbAssemblyStatsSchema>
export const dbAssemblyFieldSchema = z.object({
  id: globalSchema.mysqlCHAR,
  account_id: globalSchema.mysqlCHAR.nullable(),
  assembly_id: globalSchema.mysqlCHAR.nullable(),
  field_name: globalSchema.mysqlVARCHAR.nullable(),
  field_value: globalSchema.mysqlVARCHAR.nullable(),
  created: globalSchema.mysqlDATETIME.nullable(),
  modified: globalSchema.mysqlDATETIME.nullable(),
})
export type DbAssemblyField = z.infer<typeof dbAssemblyFieldSchema>
export const dbAssemblyTemplateSchema = z.object({
  id: globalSchema.mysqlCHAR,
  account_id: globalSchema.mysqlCHAR.nullable(),
  name: globalSchema.mysqlVARCHAR,
  json: globalSchema.mysqlTEXT,
  require_signature_auth: globalSchema.mysqlTINYINT,
  encryption_version: globalSchema.mysqlTINYINT,
  transcoding_result_expiry: globalSchema.mysqlCHAR.nullable(),
  assembly_status_expiry: globalSchema.mysqlCHAR.nullable(),
  created: globalSchema.mysqlDATETIME,
  modified: globalSchema.mysqlDATETIME,
  deleted: globalSchema.mysqlDATETIME.nullable(),
  last_used: globalSchema.mysqlDATETIME.nullable(),
})
export type DbAssemblyTemplate = z.infer<typeof dbAssemblyTemplateSchema>
export const dbAssemblyWarningSchema = z.object({
  id: globalSchema.mysqlCHAR,
  account_id: globalSchema.mysqlCHAR.nullable(),
  template_id: globalSchema.mysqlCHAR.nullable(),
  assembly_id: globalSchema.mysqlCHAR.nullable(),
  warning: globalSchema.mysqlVARCHAR,
  warning_level: globalSchema.mysqlVARCHAR,
  created: globalSchema.mysqlDATETIME,
  modified: globalSchema.mysqlDATETIME,
  deleted: globalSchema.mysqlDATETIME.nullable(),
})
export type DbAssemblyWarning = z.infer<typeof dbAssemblyWarningSchema>
export const dbAuthKeySchema = z.object({
  id: globalSchema.mysqlCHAR,
  user_id: globalSchema.mysqlCHAR.nullable(),
  type: globalSchema.mysqlVARCHAR.nullable(),
  key: globalSchema.mysqlVARCHAR,
  code: globalSchema.mysqlVARCHAR.nullable(),
  created: globalSchema.mysqlDATETIME,
  expires: globalSchema.mysqlDATETIME.nullable(),
  modified: globalSchema.mysqlDATETIME.nullable(),
})
export type DbAuthKey = z.infer<typeof dbAuthKeySchema>
export const dbBanSchema = z.object({
  id: globalSchema.mysqlCHAR,
  type: globalSchema.mysqlVARCHAR.nullable(),
  until: globalSchema.mysqlDATETIME.nullable(),
  field: globalSchema.mysqlVARCHAR.nullable(),
  value: globalSchema.mysqlVARCHAR.nullable(),
  created: globalSchema.mysqlDATETIME.nullable(),
  modified: globalSchema.mysqlDATETIME.nullable(),
  deleted: globalSchema.mysqlDATETIME.nullable(),
})
export type DbBan = z.infer<typeof dbBanSchema>
export const dbBlogPostSchema = z.object({
  id: globalSchema.mysqlCHAR,
  account_id: globalSchema.mysqlCHAR.nullable(),
  title: globalSchema.mysqlVARCHAR,
  text: globalSchema.mysqlTEXT,
  html: globalSchema.mysqlTEXT,
  url_suffix: globalSchema.mysqlVARCHAR,
  created: globalSchema.mysqlDATETIME,
  updated: globalSchema.mysqlDATETIME,
  published: globalSchema.mysqlDATETIME,
  is_published: globalSchema.mysqlTINYINT,
  deleted: globalSchema.mysqlDATETIME.nullable(),
})
export type DbBlogPost = z.infer<typeof dbBlogPostSchema>
export const dbConfigSchema = z.object({
  id: globalSchema.mysqlCHAR,
  account_id: globalSchema.mysqlCHAR,
  region: globalSchema.mysqlVARCHAR,
  cluster: globalSchema.mysqlVARCHAR,
  type: globalSchema.mysqlVARCHAR,
  config: globalSchema.mysqlTEXT.nullable(),
  created: globalSchema.mysqlDATETIME.nullable(),
  modified: globalSchema.mysqlDATETIME.nullable(),
  deleted: globalSchema.mysqlDATETIME.nullable(),
})
export type DbConfig = z.infer<typeof dbConfigSchema>
export const dbConsumedAnnouncementSchema = z.object({
  id: globalSchema.mysqlCHAR,
  user_id: globalSchema.mysqlCHAR.nullable(),
  announcement_id: globalSchema.mysqlCHAR.nullable(),
  created: globalSchema.mysqlDATETIME.nullable(),
  modified: globalSchema.mysqlDATETIME.nullable(),
})
export type DbConsumedAnnouncement = z.infer<typeof dbConsumedAnnouncementSchema>
export const dbCountrySchema = z.object({
  id: globalSchema.mysqlCHAR,
  name: globalSchema.mysqlVARCHAR,
  is_eu: globalSchema.mysqlTINYINT,
  iso_code: globalSchema.mysqlCHAR.nullable(),
  vat_rate: globalSchema.mysqlFLOAT.nullable(),
  created: globalSchema.mysqlDATETIME.nullable(),
  modified: globalSchema.mysqlDATETIME.nullable(),
  deleted: globalSchema.mysqlDATETIME.nullable(),
})
export type DbCountry = z.infer<typeof dbCountrySchema>
export const dbCouponSchema = z.object({
  id: globalSchema.mysqlCHAR,
  code: globalSchema.mysqlVARCHAR.nullable(),
  percent_reward: globalSchema.mysqlINT.nullable(),
  percent_reward_month_count: globalSchema.mysqlINT.nullable(),
  absolute_reward: globalSchema.mysqlINT.nullable(),
  beneficiary_id: globalSchema.mysqlCHAR.nullable(),
  valid_until: globalSchema.mysqlDATETIME.nullable(),
  redeemer_count: globalSchema.mysqlINT,
  max_redeemer_count: globalSchema.mysqlINT.nullable(),
  created: globalSchema.mysqlDATETIME.nullable(),
  modified: globalSchema.mysqlDATETIME.nullable(),
  deleted: globalSchema.mysqlDATETIME.nullable(),
})
export type DbCoupon = z.infer<typeof dbCouponSchema>
export const dbCredentialTypeSchema = z.object({
  id: globalSchema.mysqlVARCHAR,
  name: globalSchema.mysqlVARCHAR.nullable(),
  display_name: globalSchema.mysqlVARCHAR.nullable(),
  published: globalSchema.mysqlTINYINT,
  field_names: globalSchema.mysqlVARCHAR.nullable(),
  created: globalSchema.mysqlDATETIME.nullable(),
  modified: globalSchema.mysqlDATETIME.nullable(),
  deleted: globalSchema.mysqlDATETIME.nullable(),
})
export type DbCredentialType = z.infer<typeof dbCredentialTypeSchema>
export const dbCredentialSchema = z.object({
  id: globalSchema.mysqlVARCHAR,
  type: globalSchema.mysqlVARCHAR.nullable(),
  account_id: globalSchema.mysqlVARCHAR.nullable(),
  name: globalSchema.mysqlVARCHAR.nullable(),
  values: globalSchema.mysqlTEXT.nullable(),
  encryption_version: globalSchema.mysqlTINYINT.nullable(),
  iv: globalSchema.mysqlCHAR.nullable(),
  auth_tag: globalSchema.mysqlCHAR.nullable(),
  created: globalSchema.mysqlDATETIME.nullable(),
  modified: globalSchema.mysqlDATETIME.nullable(),
  deleted: globalSchema.mysqlDATETIME.nullable(),
  last_used: globalSchema.mysqlDATETIME.nullable(),
})
export type DbCredential = z.infer<typeof dbCredentialSchema>
export const dbCreditCardSchema = z.object({
  id: globalSchema.mysqlCHAR,
  active: globalSchema.mysqlTINYINT,
  account_id: globalSchema.mysqlCHAR.nullable(),
  owner_name: globalSchema.mysqlVARCHAR,
  preview_number: globalSchema.mysqlVARCHAR,
  expires: globalSchema.mysqlDATETIME,
  type: globalSchema.mysqlVARCHAR,
  country_code: globalSchema.mysqlCHAR,
  user_ip: globalSchema.mysqlVARCHAR.nullable(),
  user_ip_country_code: globalSchema.mysqlCHAR.nullable(),
  storage_id: globalSchema.mysqlVARCHAR,
  created: globalSchema.mysqlDATETIME,
  updated: globalSchema.mysqlDATETIME,
  deleted: globalSchema.mysqlDATETIME.nullable(),
  payment_provider: globalSchema.mysqlVARCHAR.nullable(),
})

export type DbCreditCard = z.infer<typeof dbCreditCardSchema>
export const dbCreditSchema = z.object({
  id: globalSchema.mysqlCHAR,
  account_id: globalSchema.mysqlCHAR.nullable(),
  invoice_id: globalSchema.mysqlCHAR.nullable(),
  amount: globalSchema.mysqlDECIMAL,
  currency: globalSchema.mysqlVARCHAR,
  created: globalSchema.mysqlDATETIME,
  note: globalSchema.mysqlTEXT.nullable(),
  modified: globalSchema.mysqlDATETIME.nullable(),
  deleted: globalSchema.mysqlDATETIME.nullable(),
})
export type DbCredit = z.infer<typeof dbCreditSchema>
export const dbEdglyUsageSchema = z.object({
  id: globalSchema.mysqlCHAR,
  app_name: globalSchema.mysqlVARCHAR.nullable(),
  app_id: globalSchema.mysqlCHAR.nullable(),
  status: globalSchema.mysqlCHAR.nullable(),
  year: globalSchema.mysqlCHAR.nullable(),
  month: globalSchema.mysqlCHAR.nullable(),
  bytes: globalSchema.mysqlBIGINT,
  requests: globalSchema.mysqlBIGINT,
  created: globalSchema.mysqlDATETIME,
  modified: globalSchema.mysqlDATETIME,
  deleted: globalSchema.mysqlDATETIME.nullable(),
})
export type DbEdglyUsage = z.infer<typeof dbEdglyUsageSchema>
export const dbInstanceSchema = z.object({
  id: globalSchema.mysqlCHAR,
  ec2_id: globalSchema.mysqlVARCHAR,
  spotinstance_request_id: globalSchema.mysqlVARCHAR,
  max_spot_price: globalSchema.mysqlFLOAT,
  fleet: globalSchema.mysqlVARCHAR,
  zone: globalSchema.mysqlVARCHAR,
  region: globalSchema.mysqlVARCHAR,
  hostname: globalSchema.mysqlVARCHAR,
  ec2_hostname: globalSchema.mysqlVARCHAR,
  app: globalSchema.mysqlVARCHAR,
  role: globalSchema.mysqlVARCHAR,
  cluster: globalSchema.mysqlVARCHAR,
  state: globalSchema.mysqlVARCHAR,
  is_being_replaced: globalSchema.mysqlTINYINT,
  last_state_update: globalSchema.mysqlDATETIME,
  marked_for_termination_on: globalSchema.mysqlDATETIME,
  health_check_id: globalSchema.mysqlVARCHAR,
  ami: globalSchema.mysqlVARCHAR,
  machine_type: globalSchema.mysqlVARCHAR,
  spot_type: globalSchema.mysqlVARCHAR,
  ecu_count: globalSchema.mysqlFLOAT,
  public_ip: globalSchema.mysqlVARCHAR,
  public_ipv6: globalSchema.mysqlVARCHAR,
  load: globalSchema.mysqlFLOAT,
  load_per_core: globalSchema.mysqlFLOAT,
  memory_use_percent: globalSchema.mysqlFLOAT.nullable(),
  memory: globalSchema.mysqlFLOAT.nullable(),
  disk_space_use_percent_in_root: globalSchema.mysqlFLOAT.nullable(),
  disk_space_use_percent_in_srv: globalSchema.mysqlFLOAT.nullable(),
  max_disk_space_gb_in_root: globalSchema.mysqlSMALLINT.nullable(),
  max_disk_space_gb_in_srv: globalSchema.mysqlSMALLINT.nullable(),
  batch_ratio: globalSchema.mysqlFLOAT,
  backup_ratio: globalSchema.mysqlFLOAT,
  discounted_ratio: globalSchema.mysqlFLOAT,
  privswitch_ratio: globalSchema.mysqlFLOAT,
  test_ratio: globalSchema.mysqlFLOAT.nullable(),
  build_id: globalSchema.mysqlVARCHAR,
  build_url: globalSchema.mysqlVARCHAR,
  last_seen: globalSchema.mysqlDATETIME,
  log_level: globalSchema.mysqlTINYINT,
  db_in_sync_till: globalSchema.mysqlDATETIME,
  outstanding_bytes: globalSchema.mysqlBIGINT,
  created: globalSchema.mysqlDATETIME,
  active_assemblies: globalSchema.mysqlINT,
  updated: globalSchema.mysqlDATETIME,
  is_iron: globalSchema.mysqlTINYINT,
  pid: globalSchema.mysqlINT,
  last_cluster_update: globalSchema.mysqlDATETIME,
  private_ip: globalSchema.mysqlVARCHAR,
  private_ipv6: globalSchema.mysqlVARCHAR,
  last_deploy_start: globalSchema.mysqlDATETIME,
  last_deploy_end: globalSchema.mysqlDATETIME,
})
export type DbInstance = z.infer<typeof dbInstanceSchema>
export const dbInstanceHistoryEntriesSchema = z.object({
  id: globalSchema.mysqlCHAR,
  region: globalSchema.mysqlVARCHAR.nullable(),
  num_basis_uploader_ecus: globalSchema.mysqlSMALLINT.nullable(),
  max_basis_uploader_load_per_core: globalSchema.mysqlDECIMAL,
  median_basis_uploader_load_per_core: globalSchema.mysqlDECIMAL,
  min_basis_uploader_load_per_core: globalSchema.mysqlDECIMAL,
  num_basis_drone_ecus: globalSchema.mysqlSMALLINT.nullable(),
  max_basis_drone_load_per_core: globalSchema.mysqlDECIMAL,
  median_basis_drone_load_per_core: globalSchema.mysqlDECIMAL,
  min_basis_drone_load_per_core: globalSchema.mysqlDECIMAL,
  num_basis_spot_drone_ecus: globalSchema.mysqlSMALLINT.nullable(),
  max_basis_spot_drone_load_per_core: globalSchema.mysqlDECIMAL,
  median_basis_spot_drone_load_per_core: globalSchema.mysqlDECIMAL,
  min_basis_spot_drone_load_per_core: globalSchema.mysqlDECIMAL,
  num_reinforce_drone_ecus: globalSchema.mysqlSMALLINT.nullable(),
  max_reinforce_drone_load_per_core: globalSchema.mysqlDECIMAL,
  median_reinforce_drone_load_per_core: globalSchema.mysqlDECIMAL,
  min_reinforce_drone_load_per_core: globalSchema.mysqlDECIMAL,
  num_urltransform_drone_ecus: globalSchema.mysqlSMALLINT.nullable(),
  max_urltransform_drone_load_per_core: globalSchema.mysqlDECIMAL,
  median_urltransform_drone_load_per_core: globalSchema.mysqlDECIMAL,
  min_urltransform_drone_load_per_core: globalSchema.mysqlDECIMAL,
  num_reinforce_spot_drone_ecus: globalSchema.mysqlSMALLINT.nullable(),
  max_reinforce_spot_drone_load_per_core: globalSchema.mysqlDECIMAL,
  median_reinforce_spot_drone_load_per_core: globalSchema.mysqlDECIMAL,
  min_reinforce_spot_drone_load_per_core: globalSchema.mysqlDECIMAL,
  num_urltransform_spot_drone_ecus: globalSchema.mysqlSMALLINT.nullable(),
  max_urltransform_spot_drone_load_per_core: globalSchema.mysqlDECIMAL,
  median_urltransform_spot_drone_load_per_core: globalSchema.mysqlDECIMAL,
  min_urltransform_spot_drone_load_per_core: globalSchema.mysqlDECIMAL,
  created: globalSchema.mysqlDATETIME.nullable(),
  modified: globalSchema.mysqlDATETIME.nullable(),
  deleted: globalSchema.mysqlDATETIME.nullable(),
})
export type DbInstanceHistoryEntries = z.infer<typeof dbInstanceHistoryEntriesSchema>
export const dbInvitationSchema = z.object({
  id: globalSchema.mysqlCHAR,
  user_id: globalSchema.mysqlCHAR.nullable(),
  account_id: globalSchema.mysqlCHAR.nullable(),
  role: globalSchema.mysqlVARCHAR.nullable(),
  name: globalSchema.mysqlVARCHAR.nullable(),
  email: globalSchema.mysqlVARCHAR.nullable(),
  created: globalSchema.mysqlDATETIME.nullable(),
  modified: globalSchema.mysqlDATETIME.nullable(),
  deleted: globalSchema.mysqlDATETIME.nullable(),
})
export type DbInvitation = z.infer<typeof dbInvitationSchema>
export const dbInvoiceNotificationSchema = z.object({
  id: globalSchema.mysqlCHAR,
  account_id: globalSchema.mysqlCHAR.nullable(),
  month: globalSchema.mysqlCHAR,
  created: globalSchema.mysqlDATETIME,
  modified: globalSchema.mysqlDATETIME.nullable(),
  deleted: globalSchema.mysqlDATETIME.nullable(),
})
export type DbInvoiceNotification = z.infer<typeof dbInvoiceNotificationSchema>
export const dbInvoicePaymentSchema = z.object({
  id: globalSchema.mysqlCHAR,
  invoice_id: globalSchema.mysqlCHAR.nullable(),
  account_id: globalSchema.mysqlCHAR.nullable(),
  credit_card_id: globalSchema.mysqlCHAR.nullable(),
  amount: globalSchema.mysqlFLOAT,
  currency: globalSchema.mysqlVARCHAR,
  authcode: globalSchema.mysqlVARCHAR.nullable(),
  created: globalSchema.mysqlDATETIME,
  modified: globalSchema.mysqlDATETIME.nullable(),
  deleted: globalSchema.mysqlDATETIME.nullable(),
})
export type DbInvoicePayment = z.infer<typeof dbInvoicePaymentSchema>
export const dbInvoiceSchema = z.object({
  id: globalSchema.mysqlCHAR,
  int_id: globalSchema.mysqlINT,
  number: globalSchema.mysqlVARCHAR,
  paid: globalSchema.mysqlDATETIME.nullable(),
  authcode: globalSchema.mysqlVARCHAR,
  account_id: globalSchema.mysqlCHAR.nullable(),
  month: globalSchema.mysqlVARCHAR,
  plan_id: globalSchema.mysqlCHAR.nullable(),
  gb_included: globalSchema.mysqlFLOAT,
  price_per_gb: globalSchema.mysqlFLOAT,
  price_per_month: globalSchema.mysqlFLOAT,
  used_gb: globalSchema.mysqlFLOAT,
  additional_gb: globalSchema.mysqlFLOAT,
  base_fee: globalSchema.mysqlFLOAT,
  additional_gb_fee: globalSchema.mysqlFLOAT,
  num_machines: globalSchema.mysqlINT.nullable(),
  price_per_machine: globalSchema.mysqlDECIMAL.nullable(),
  custom_expenses: globalSchema.mysqlTEXT.nullable(),
  subtotal: globalSchema.mysqlFLOAT,
  robots: globalSchema.mysqlTEXT,
  tiers: globalSchema.mysqlTEXT.nullable(),
  reverse_charge_vat: globalSchema.mysqlTINYINT,
  vat_rate: globalSchema.mysqlFLOAT,
  vat: globalSchema.mysqlDECIMAL,
  credit: globalSchema.mysqlDECIMAL,
  total_fee: globalSchema.mysqlFLOAT,
  currency: globalSchema.mysqlVARCHAR,
  to_name: globalSchema.mysqlVARCHAR.nullable(),
  to_company: globalSchema.mysqlVARCHAR.nullable(),
  to_contact_email_address: globalSchema.mysqlVARCHAR.nullable(),
  to_address_1: globalSchema.mysqlVARCHAR.nullable(),
  to_address_2: globalSchema.mysqlVARCHAR.nullable(),
  to_zip: globalSchema.mysqlVARCHAR.nullable(),
  to_city: globalSchema.mysqlVARCHAR.nullable(),
  to_country_id: globalSchema.mysqlCHAR.nullable(),
  to_vat_id: globalSchema.mysqlVARCHAR.nullable(),
  po_number: globalSchema.mysqlVARCHAR.nullable(),
  created: globalSchema.mysqlDATETIME,
  modified: globalSchema.mysqlDATETIME,
  deleted: globalSchema.mysqlDATETIME.nullable(),
  reward_discount: globalSchema.mysqlDECIMAL,
  reward_discount_percent: globalSchema.mysqlDECIMAL,
  wont_get: globalSchema.mysqlTINYINT,
  signup_discount: globalSchema.mysqlDECIMAL,
  signup_discount_percent: globalSchema.mysqlDECIMAL,
  coupon_discount: globalSchema.mysqlDECIMAL,
  coupon_discount_percent: globalSchema.mysqlDECIMAL,
  uses_invoicing: globalSchema.mysqlTINYINT,
})
export type DbInvoice = z.infer<typeof dbInvoiceSchema>
export const dbLoginTokenSchema = z.object({
  id: globalSchema.mysqlCHAR,
  user_id: globalSchema.mysqlCHAR.nullable(),
  token: globalSchema.mysqlCHAR.nullable(),
  duration: globalSchema.mysqlVARCHAR,
  used: globalSchema.mysqlTINYINT,
  created: globalSchema.mysqlDATETIME,
  expires: globalSchema.mysqlDATETIME,
  modified: globalSchema.mysqlDATETIME.nullable(),
  deleted: globalSchema.mysqlDATETIME.nullable(),
})
export type DbLoginToken = z.infer<typeof dbLoginTokenSchema>
export const dbOldSlugSchema = z.object({
  id: globalSchema.mysqlBIGINT,
  slug: globalSchema.mysqlVARCHAR,
  created_at: globalSchema.mysqlTIMESTAMP,
  foreign_id: globalSchema.mysqlCHAR,
  foreign_table: globalSchema.mysqlCHAR,
  created: globalSchema.mysqlDATETIME.nullable(),
})
export type DbOldSlug = z.infer<typeof dbOldSlugSchema>
export const dbOssSupportPingSchema = z.object({
  id: globalSchema.mysqlCHAR,
  service: globalSchema.mysqlVARCHAR.describe("e.g. ''companion''"),
  version: globalSchema.mysqlVARCHAR.describe(
    "''2.0.1'', or whatever is inside the service that is reporting''s package.json",
  ),
  app_id: globalSchema.mysqlVARCHAR.describe(
    'corresponds with account.id (account is called app these days)',
  ),
  process_id: globalSchema.mysqlVARCHAR.describe(
    'a UUID kept inside Companion process memory and will live for as long as it does, allowing us to group pings',
  ),
  created: globalSchema.mysqlDATETIME.describe(
    'timestamp (this is a mandatory field that lets us sync records in a master-master way with dbsyncer.js, required when doing migrations)',
  ),
  modified: globalSchema.mysqlDATETIME.describe(
    'same timestamp (this is a mandatory field that lets us sync records in a master-master way with dbsyncer.js, required when doing migrations)',
  ),
  deleted: globalSchema.mysqlDATETIME
    .nullable()
    .describe(
      'this is a mandatory field that lets us sync records in a master-master way with dbsyncer.js, required when doing migrations',
    ),
})
export type DbOssSupportPing = z.infer<typeof dbOssSupportPingSchema>
export const dbOssSupportPing3Schema = z.object({
  id: globalSchema.mysqlCHAR,
  service: globalSchema.mysqlVARCHAR.describe("e.g. ''companion''"),
  version: globalSchema.mysqlVARCHAR.describe(
    "''2.0.1'', or whatever is inside the service that is reporting''s package.json",
  ),
  app_id: globalSchema.mysqlVARCHAR.describe(
    'corresponds with account.id (account is called app these days)',
  ),
  process_id: globalSchema.mysqlVARCHAR.describe(
    'a UUID kept inside Companion process memory and will live for as long as it does, allowing us to group pings',
  ),
  created: globalSchema.mysqlDATETIME.describe(
    'timestamp (this is a mandatory field that lets us sync records in a master-master way with dbsyncer.js, required when doing migrations)',
  ),
  created_ts: globalSchema.mysqlINT
    .nullable()
    .describe(
      'timestamp (this is a mandatory field that lets us sync records in a master-master way with dbsyncer.js, required when doing migrations)',
    ),
  modified: globalSchema.mysqlDATETIME.describe(
    'same timestamp (this is a mandatory field that lets us sync records in a master-master way with dbsyncer.js, required when doing migrations)',
  ),
  deleted: globalSchema.mysqlDATETIME
    .nullable()
    .describe(
      'this is a mandatory field that lets us sync records in a master-master way with dbsyncer.js, required when doing migrations',
    ),
})
export type DbOssSupportPing3 = z.infer<typeof dbOssSupportPing3Schema>
export const dbPaymentHistorySchema = z.object({
  id: globalSchema.mysqlCHAR,
  invoice_id: globalSchema.mysqlCHAR.nullable(),
  account_id: globalSchema.mysqlCHAR.nullable(),
  authcode: globalSchema.mysqlVARCHAR,
  amount: globalSchema.mysqlFLOAT,
  currency: globalSchema.mysqlVARCHAR,
  storage_id: globalSchema.mysqlVARCHAR,
  success: globalSchema.mysqlTINYINT,
  error: globalSchema.mysqlVARCHAR,
  response: globalSchema.mysqlTEXT,
  created: globalSchema.mysqlDATETIME,
  modified: globalSchema.mysqlDATETIME.nullable(),
  deleted: globalSchema.mysqlDATETIME.nullable(),
  payment_provider: globalSchema.mysqlVARCHAR.nullable(),
  stripe_payment_intent_id: globalSchema.mysqlTEXT.nullable(),
  stripe_client_secret: globalSchema.mysqlTEXT.nullable(),
})
export type DbPaymentHistory = z.infer<typeof dbPaymentHistorySchema>
export const dbPlanChangeSchema = z.object({
  id: globalSchema.mysqlCHAR,
  type: globalSchema.mysqlVARCHAR,
  old_plan_id: globalSchema.mysqlCHAR.nullable(),
  new_plan_id: globalSchema.mysqlCHAR.nullable(),
  account_id: globalSchema.mysqlCHAR.nullable(),
  reason: globalSchema.mysqlTEXT,
  created: globalSchema.mysqlDATETIME,
  modified: globalSchema.mysqlDATETIME.nullable(),
  deleted: globalSchema.mysqlDATETIME.nullable(),
})
export type DbPlanChange = z.infer<typeof dbPlanChangeSchema>
export const dbPlanSchema = z.object({
  id: globalSchema.mysqlCHAR,
  title: globalSchema.mysqlVARCHAR,
  slug: globalSchema.mysqlVARCHAR,
  price_per_month: globalSchema.mysqlDECIMAL,
  gb_included: globalSchema.mysqlDECIMAL,
  gb_limit: globalSchema.mysqlDECIMAL.nullable(),
  file_size_limit_in_gb: globalSchema.mysqlDECIMAL.nullable(),
  max_jobs_simulteneous: globalSchema.mysqlTINYINT.nullable(),
  num_concurrent_priority_jobs: globalSchema.mysqlINT.nullable(),
  num_concurrent_batch_job_slots: globalSchema.mysqlINT.nullable(),
  num_seats: globalSchema.mysqlTINYINT,
  has_lifetime_limit: globalSchema.mysqlTINYINT,
  price_per_gb: globalSchema.mysqlDECIMAL,
  tiers: globalSchema.mysqlTEXT.nullable(),
  currency: globalSchema.mysqlVARCHAR,
  usd_exchange_rate: globalSchema.mysqlDECIMAL.nullable(),
  published: globalSchema.mysqlTINYINT,
  can_use_image_turbo_mode: globalSchema.mysqlTINYINT.nullable(),
  can_use_video_turbo_mode: globalSchema.mysqlTINYINT.nullable(),
  can_use_assembly_fields_search: globalSchema.mysqlTINYINT.nullable(),
  should_watermark_transcoding_results: globalSchema.mysqlTINYINT,
  comments: globalSchema.mysqlTEXT,
  created: globalSchema.mysqlDATETIME.nullable(),
  modified: globalSchema.mysqlDATETIME.nullable(),
  deleted: globalSchema.mysqlDATETIME.nullable(),
  num_machines: globalSchema.mysqlINT.nullable(),
  price_per_machine: globalSchema.mysqlDECIMAL.nullable(),
})
export type DbPlan = z.infer<typeof dbPlanSchema>
export const dbRedeemedCouponSchema = z.object({
  id: globalSchema.mysqlCHAR,
  coupon_id: globalSchema.mysqlCHAR.nullable(),
  account_id: globalSchema.mysqlCHAR.nullable(),
  redeemed_on: globalSchema.mysqlDATETIME.nullable(),
  valid_until: globalSchema.mysqlDATETIME.nullable(),
  deleted: globalSchema.mysqlDATETIME.nullable(),
  created: globalSchema.mysqlDATETIME.nullable(),
})
export type DbRedeemedCoupon = z.infer<typeof dbRedeemedCouponSchema>
export const dbRewardSchema = z.object({
  id: globalSchema.mysqlCHAR,
  account_id: globalSchema.mysqlCHAR,
  type: globalSchema.mysqlVARCHAR,
  status: globalSchema.mysqlVARCHAR,
  referred_account_id: globalSchema.mysqlCHAR,
  invited_email: globalSchema.mysqlVARCHAR,
  discount: globalSchema.mysqlINT,
  twitter_username: globalSchema.mysqlVARCHAR,
  tweet_text: globalSchema.mysqlTEXT,
  is_referer: globalSchema.mysqlTINYINT.nullable(),
  created: globalSchema.mysqlDATETIME,
  modified: globalSchema.mysqlDATETIME,
})
export type DbReward = z.infer<typeof dbRewardSchema>
export const dbSessionSchema = z.object({
  id: globalSchema.mysqlCHAR,
  user_id: globalSchema.mysqlCHAR.nullable(),
  created: globalSchema.mysqlDATETIME.nullable(),
  modified: globalSchema.mysqlDATETIME.nullable(),
  data: globalSchema.mysqlBLOB.nullable(),
  expires: globalSchema.mysqlINT.nullable(),
})
export type DbSession = z.infer<typeof dbSessionSchema>
export const dbSignatureSchema = z.object({
  int_id: globalSchema.mysqlINT,
  id: globalSchema.mysqlCHAR,
  account_id: globalSchema.mysqlCHAR.nullable(),
  endpoint: globalSchema.mysqlVARCHAR,
  path: globalSchema.mysqlVARCHAR.nullable(),
  method: globalSchema.mysqlVARCHAR.nullable(),
  value: globalSchema.mysqlVARCHAR.nullable(),
  expires_on: globalSchema.mysqlINT,
  expires_on_human: globalSchema.mysqlDATETIME.nullable(),
  created: globalSchema.mysqlDATETIME.nullable(),
  created_ts: globalSchema.mysqlINT,
  updated: globalSchema.mysqlDATETIME.nullable(),
})
export type DbSignature = z.infer<typeof dbSignatureSchema>
export const dbSubscriberSchema = z.object({
  id: globalSchema.mysqlCHAR,
  name: globalSchema.mysqlVARCHAR,
  email: globalSchema.mysqlVARCHAR,
  has_invalid_email: globalSchema.mysqlTINYINT,
  ip: globalSchema.mysqlVARCHAR,
  is_subscribed: globalSchema.mysqlTINYINT,
  created: globalSchema.mysqlDATETIME,
  modified: globalSchema.mysqlDATETIME,
  deleted: globalSchema.mysqlDATETIME.nullable(),
})
export type DbSubscriber = z.infer<typeof dbSubscriberSchema>
export const dbTestimonialSchema = z.object({
  id: globalSchema.mysqlCHAR,
  body: globalSchema.mysqlTEXT,
  by: globalSchema.mysqlVARCHAR,
  source_name: globalSchema.mysqlVARCHAR,
  source_url: globalSchema.mysqlVARCHAR,
  published: globalSchema.mysqlTINYINT,
  created: globalSchema.mysqlDATETIME,
  modified: globalSchema.mysqlDATETIME.nullable(),
  deleted: globalSchema.mysqlDATETIME.nullable(),
})
export type DbTestimonial = z.infer<typeof dbTestimonialSchema>
export const dbTlcdnUsageSchema = z.object({
  id: globalSchema.mysqlCHAR,
  app_name: globalSchema.mysqlVARCHAR.nullable(),
  app_id: globalSchema.mysqlCHAR.nullable(),
  status: globalSchema.mysqlCHAR.nullable(),
  year: globalSchema.mysqlCHAR.nullable(),
  month: globalSchema.mysqlCHAR.nullable(),
  bytes: globalSchema.mysqlBIGINT,
  requests: globalSchema.mysqlBIGINT,
  created: globalSchema.mysqlDATETIME,
  modified: globalSchema.mysqlDATETIME,
  deleted: globalSchema.mysqlDATETIME.nullable(),
})
export type DbTlcdnUsage = z.infer<typeof dbTlcdnUsageSchema>
export const dbTranscodingQueueSchema = z.object({
  int_id: globalSchema.mysqlINT,
  id: globalSchema.mysqlCHAR,
  queue_type: globalSchema.mysqlCHAR.nullable(),
  slot_size: globalSchema.mysqlINT.nullable(),
  length: globalSchema.mysqlINT,
  wait: globalSchema.mysqlINT.nullable(),
  bytes: globalSchema.mysqlINT,
  duration: globalSchema.mysqlINT,
  num_jobs_by_customer: globalSchema.mysqlTEXT,
  num_ecu_running: globalSchema.mysqlINT.nullable(),
  num_ecu_launching: globalSchema.mysqlINT.nullable(),
  region: globalSchema.mysqlVARCHAR.nullable(),
  cluster: globalSchema.mysqlVARCHAR.nullable(),
  created: globalSchema.mysqlDATETIME,
  created_ts: globalSchema.mysqlINT,
})
export type DbTranscodingQueue = z.infer<typeof dbTranscodingQueueSchema>
export const dbUsageSchema = z.object({
  id: globalSchema.mysqlCHAR,
  account_id: globalSchema.mysqlCHAR.nullable(),
  robot: globalSchema.mysqlVARCHAR,
  robot_factor: globalSchema.mysqlDECIMAL,
  bytes_in: globalSchema.mysqlBIGINT,
  bytes_out: globalSchema.mysqlBIGINT,
  free: globalSchema.mysqlTINYINT,
  discount_queue: globalSchema.mysqlTINYINT,
  https: globalSchema.mysqlTINYINT,
  month: globalSchema.mysqlDATE,
  region: globalSchema.mysqlVARCHAR,
  tags: globalSchema.mysqlVARCHAR,
  created: globalSchema.mysqlDATETIME,
  modified: globalSchema.mysqlDATETIME,
  deleted: globalSchema.mysqlDATETIME.nullable(),
})
export type DbUsage = z.infer<typeof dbUsageSchema>
export const dbUserSchema = z.object({
  id: globalSchema.mysqlCHAR,
  active_account_id: globalSchema.mysqlCHAR.nullable(),
  name: globalSchema.mysqlVARCHAR.nullable(),
  email: globalSchema.mysqlVARCHAR.nullable(),
  email_verified: globalSchema.mysqlTINYINT,
  transloadit_role: globalSchema.mysqlVARCHAR,
  transloadit_sshkey: globalSchema.mysqlVARCHAR,
  two_factor_auth_type: globalSchema.mysqlVARCHAR,
  pending_email_change: globalSchema.mysqlVARCHAR.nullable(),
  is_oauth: globalSchema.mysqlTINYINT,
  password_hash: globalSchema.mysqlVARCHAR.nullable(),
  password_hash_version: globalSchema.mysqlTINYINT.nullable(),
  last_login: globalSchema.mysqlDATETIME.nullable(),
  send_newsletters: globalSchema.mysqlTINYINT,
  skype_name: globalSchema.mysqlVARCHAR,
  cancelled: globalSchema.mysqlDATETIME.nullable(),
  cancel_reason: globalSchema.mysqlTEXT,
  created: globalSchema.mysqlDATETIME,
  modified: globalSchema.mysqlDATETIME,
  deleted: globalSchema.mysqlDATETIME.nullable(),
  is_student: globalSchema.mysqlTINYINT,
  is_teacher: globalSchema.mysqlTINYINT,
  received_welcome_email: globalSchema.mysqlTINYINT.nullable(),
  has_active_alarm_bell: globalSchema.mysqlTINYINT,
  github_token: globalSchema.mysqlVARCHAR,
  timezone: globalSchema.mysqlVARCHAR.nullable(),
})
export type DbUser = z.infer<typeof dbUserSchema>
export const dbVisitorOriginSchema = z.object({
  id: globalSchema.mysqlCHAR,
  from: globalSchema.mysqlVARCHAR,
  referer: globalSchema.mysqlVARCHAR,
  account_id: globalSchema.mysqlCHAR.nullable(),
  q: globalSchema.mysqlVARCHAR,
  chan: globalSchema.mysqlVARCHAR,
  target: globalSchema.mysqlVARCHAR,
  created: globalSchema.mysqlDATETIME,
  modified: globalSchema.mysqlDATETIME.nullable(),
  deleted: globalSchema.mysqlDATETIME.nullable(),
})
export type DbVisitorOrigin = z.infer<typeof dbVisitorOriginSchema>
